import { z } from "zod";

/**
 * One competency's configuration FOR THIS ROUND (Figma 02.5 · 331:25806, right panel).
 *
 * Two fields, because the design offers exactly two controls: a toggle for whether the round
 * evidences this competency at all, and an « Obligatoire / Optionnel » dropdown.
 *
 * ⚠ NO WEIGHT, NO MINIMUM SCORE, NO ELIMINATORY FLAG — and that is the point. They belong to the
 * selection campaign in SELECT, which does not exist yet, and US-E1-03 is explicit: "selecting a
 * competency does not set a weight, a minimum score or an eliminatory flag — those belong to the
 * selection campaign and are not available here". The backend disagrees and requires a weight; see
 * `toCompetencyConfigBody` for how that is satisfied without ever showing one.
 *
 * Identified by `code`, not by the per-release competency `id`, so a draft survives the framework
 * being republished underneath it — which happens on every competency edit.
 */
export const RoundCompetencyDraftSchema = z.object({
  /** The competency's stable code — the identity that carries across framework releases. */
  code: z.string(),
  /** Whether this round evidences the competency at all. The design's toggle. */
  is_selected: z.boolean(),
  /**
   * Whether coverage is REQUIRED for this round. The design's « Obligatoire / Optionnel ».
   *
   * A mandatory competency with no required question evidencing it blocks scheduling
   * (US-E10-07), which is what the empty-group warning in the questions panel is about.
   */
  is_mandatory: z.boolean(),
});

export type RoundCompetencyDraft = z.infer<typeof RoundCompetencyDraftSchema>;

/** One row of `GET /intake-rounds/{id}/competency-framework/`. */
export const RoundCompetencyConfigSchema = z.object({
  id: z.string(),
  competency_id: z.string(),
  code: z.string(),
  side: z.enum(["FOUNDER", "VENTURE"]),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  is_optional_signal: z.boolean(),
  is_selected: z.boolean(),
  is_mandatory: z.boolean(),
  /** Read but never rendered — see the note on `RoundCompetencyDraftSchema`. */
  weight: z.number(),
  display_order: z.number().int(),
});

export type RoundCompetencyConfig = z.infer<typeof RoundCompetencyConfigSchema>;

/** Server row -> editable draft. Drops everything SELECT owns. */
export function toRoundCompetencyDraft(
  row: RoundCompetencyConfig
): RoundCompetencyDraft {
  return {
    code: row.code,
    is_selected: row.is_selected,
    is_mandatory: row.is_mandatory,
  };
}

/**
 * A competency the round has never been configured for.
 *
 * `is_selected` follows the LIBRARY's own `default_mandatory`/optional-signal shape rather than
 * defaulting everything on: an optional signal is carried in the catalogue precisely because it is
 * not scored, so opting a round into it should be a choice. Everything else starts included, because
 * a round that evidences nothing is the failure US-E10-07 exists to prevent.
 */
export function defaultRoundCompetency(competency: {
  code: string;
  default_mandatory: boolean;
  is_optional_signal: boolean;
}): RoundCompetencyDraft {
  return {
    code: competency.code,
    is_selected: !competency.is_optional_signal,
    is_mandatory: competency.default_mandatory,
  };
}

/** One row of the `PUT` body. `weight` is required by the serializer; see below. */
interface CompetencyConfigWrite {
  competency_id: string;
  is_selected: boolean;
  is_mandatory: boolean;
  weight: number;
}

/**
 * Map the draft onto the `PUT /competency-framework/` body.
 *
 * ⚠ **THE WEIGHT IS SYNTHESISED, AND IT IS A PLACEHOLDER.** `RoundCompetencyConfigSerializer` makes
 * `weight` required with no default, and `replace_round_competency_configs` refuses unless the
 * SELECTED rows total exactly 100 per side:
 *
 * ```python
 * if any(abs(value - 100.0) >= 0.01 for value in selected_weights.values()):
 *     raise AcquireError("competency_weights_must_total_100", 400, ...)
 * ```
 *
 * So a body carrying only the design's two fields is a 400. The weight is therefore distributed
 * EVENLY across the selected competencies on each side — the one distribution that expresses no
 * opinion, since any other would be the frontend inventing scoring policy.
 *
 * This is a deliberate deviation with a known owner. Ruling D3 and US-E1-07 both say weights belong
 * to the selection campaign and must not be in the round-open snapshot; Aymen's
 * `05_CONSTRAINTS_AND_INVARIANTS.md` lists them as round configuration "validated before publish",
 * which is what he built. Akram's call is that weights are SELECT's problem and must not reach the
 * administrator. Until the backend makes `weight` optional and moves the total to SELECT, this keeps
 * that true.
 *
 * The remainder goes to the FIRST row rather than being spread, so the total is exactly 100 rather
 * than 99.99 — the check uses a 0.01 tolerance and three selected competencies would otherwise fail.
 */
export function toCompetencyConfigBody(
  drafts: readonly RoundCompetencyDraft[],
  competencyIdByCode: ReadonlyMap<string, { id: string; side: string }>
): CompetencyConfigWrite[] {
  const selectedBySide = new Map<string, number>();
  for (const draft of drafts) {
    const meta = competencyIdByCode.get(draft.code);
    if (!draft.is_selected || !meta) continue;
    selectedBySide.set(meta.side, (selectedBySide.get(meta.side) ?? 0) + 1);
  }

  const assignedBySide = new Map<string, number>();

  return drafts.flatMap((draft) => {
    const meta = competencyIdByCode.get(draft.code);
    if (!meta) return [];

    let weight = 0;
    if (draft.is_selected) {
      const count = selectedBySide.get(meta.side) ?? 1;
      const seen = assignedBySide.get(meta.side) ?? 0;
      const share = Math.floor((100 / count) * 100) / 100;
      // First row on this side absorbs the rounding remainder, so the side lands on exactly 100.
      weight =
        seen === 0 ? Number((100 - share * (count - 1)).toFixed(2)) : share;
      assignedBySide.set(meta.side, seen + 1);
    }

    return [
      {
        competency_id: meta.id,
        is_selected: draft.is_selected,
        is_mandatory: draft.is_mandatory,
        weight,
      },
    ];
  });
}
